﻿<template>
  <v-app>
    <!-- WebGL 배경 효과 -->
    <WebGLBackground />

    <Header />
    <v-main class="main-content">
      <router-view />
    </v-main>
    <Footer />
  </v-app>
</template>

<script setup>
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import WebGLBackground from './components/WebGLBackground.vue'
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Noto Sans KR', sans-serif;
  line-height: 1.6;
  color: #e0e0e0;
  background-color: #0a0a0a;
}

/* 브랜드 컬러 변수 - 포인트 컬러로만 사용 */
:root {
  /* === 브랜드 컬러 === */
  --mocha-mousse: #a47864; /* 메인 포인트 컬러 */
  --tendrill: #8ba475;
  --rose-tan: #d19c97;
  --cornflower-blue: #7391C8;
  --viola: #A692BA;
  --cobblestone: #a89a8e;
  --willow: #9A8B4F;
  --gardenia: #f0e9e0;

  /* === 스케줄 타입별 색상 === */
  --green-light: #8ba475;
  --blue-light: #7391C8;
  --purple-light: #A692BA;

  /* === 블랙톤 색상 === */
  --black-primary: #0a0a0a;
  --black-secondary: #1a1a1a;
  --black-tertiary: #2a2a2a;
  --black-surface: #1e1e1e;
  --black-card: #2d2d2d;
  --gray-light: #3a3a3a;
  --gray-medium: #666666;
  --gray-dark: #888888;
  --white-primary: #ffffff;
  --white-secondary: #e0e0e0;
  --white-tertiary: #cccccc;

  /* === 타이포그래피 - 폰트 패밀리 === */
  --font-primary: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-display: 'Paperlogy-8ExtraBold', var(--font-primary);
  --font-accent: 'Ownglyph_daelong-Rg', var(--font-primary);

  /* === 타이포그래피 - 폰트 사이즈 (모바일 우선) === */
  --text-xs: 0.75rem;     /* 12px */
  --text-sm: 0.875rem;    /* 14px */
  --text-base: 1rem;      /* 16px */
  --text-lg: 1.125rem;    /* 18px */
  --text-xl: 1.25rem;     /* 20px */
  --text-2xl: 1.5rem;     /* 24px */
  --text-3xl: 1.875rem;   /* 30px */
  --text-4xl: 2.25rem;    /* 36px */

  /* === 타이포그래피 - 폰트 웨이트 === */
  --font-normal: 400;
  --font-medium: 500;
  --font-semibold: 600;
  --font-bold: 700;

  /* === 타이포그래피 - 라인 하이트 === */
  --leading-tight: 1.25;
  --leading-normal: 1.5;
  --leading-relaxed: 1.75;

  /* === 그라데이션 === */
  --gradient-primary: linear-gradient(135deg, var(--mocha-mousse) 0%, var(--rose-tan) 50%, var(--tendrill) 100%);
  --gradient-subtle: linear-gradient(135deg, rgba(164, 120, 100, 0.1) 0%, rgba(139, 164, 117, 0.1) 100%);

  /* === 간격 === */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  --space-2xl: 3rem;
}

/* Vuetify 배경 투명하게 - WebGL 배경이 보이도록 */
.v-application {
  background: transparent !important;
}

.v-main {
  background: transparent !important;
}

/* Vuetify 컨테이너 투명 */
.v-container {
  background: transparent !important;
}

/* Vuetify 카드 반투명 - WebGL 배경이 비치도록 */
.v-card.bg-black-card,
.v-card[class*="black-card"] {
  background: rgba(20, 20, 20, 0.85) !important;
  backdrop-filter: blur(10px);
}

/* 메인 콘텐츠 - WebGL 배경 위에 표시 */
.main-content {
  position: relative;
  z-index: 1;
  background: transparent !important;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: 0 4px 15px rgba(164, 120, 100, 0.4);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 25px rgba(164, 120, 100, 0.6);
}

.btn-secondary {
  background-color: var(--black-tertiary);
  color: var(--white-secondary);
  border: 2px solid var(--mocha-mousse);
}

.btn-secondary:hover {
  background-color: var(--mocha-mousse);
  color: white;
}

.section {
  padding: 80px 0;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--white-primary);
}

.section-subtitle {
  text-align: center;
  font-size: 1.2rem;
  color: var(--gray-dark);
  margin-bottom: 60px;
}

/* 그라데이션 텍스트 */
.gradient-text {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 프리미엄 카드 스타일 */
.premium-card {
  background: rgba(45, 45, 45, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(164, 120, 100, 0.2);
  border-radius: 20px;
  transition: all 0.4s ease;
}

.premium-card:hover {
  transform: translateY(-8px);
  border-color: var(--mocha-mousse);
  box-shadow: 0 20px 40px rgba(164, 120, 100, 0.2);
}

/* 애니메이션 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeInUp 0.8s ease forwards;
}

/* 글로우 효과 */
.glow {
  box-shadow: 0 0 20px rgba(164, 120, 100, 0.4);
}

.glow-text {
  text-shadow: 0 0 20px rgba(164, 120, 100, 0.6);
}

/* Footer 스타일 오버라이드 */
.v-footer {
  min-height: auto !important;
  height: auto !important;
  flex: 0 0 auto !important;
}
</style>